/**
 * W5 — Operative program. The ordered sequence of phases that governs the plan
 * curve (depth vs. days) read by Oxbo-Z §13 + the §21d reconciler. Authors edit
 * each phase in place (time/DOL · depth from/until · %/amount), reorder the
 * sequence, and the accumulated-time column + the plan curve re-derive live.
 * Wired to the pApi `programa` dataset (GET datasets / PUT programa), MSW-seeded.
 * Built to w5-operative-program.html on the P3 kit.
 */
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import { useGetPlanQuery, useGetPlanDatasetsQuery, usePutProgramaMutation } from "@api/pApi";
import type { ProgramaStep } from "@api/types";
import { QueryState } from "@components/ui/QueryState";
import { InlineRowGrid, type GridColumn } from "@components/input/InlineRowGrid";
import { enrichEventCode, EVENT_LETTER_OP_VAR } from "@lib/eventEnrichment";
import { fmtNum, fmtMoney, fmtPct } from "@lib/format";
import {
  withDerived,
  programTotals,
  curvePoints,
  emptyStep,
  type DerivedStep,
} from "./programModel";
import { PlanCurveChart } from "./PlanCurveChart";
import "./program.css";

/** Fallback plan the screen opens against when W0 hasn't set an active plan. */
const DEFAULT_PLAN_ID = "pl-fp1216-pnc";

export function ProgramScreen() {
  const { t, locale } = useLocale();
  const activePlanId = useAppSelector((s) => s.wellContext.activePlanId);
  const planId = activePlanId ?? DEFAULT_PLAN_ID;

  const planQ = useGetPlanQuery(planId);
  const datasetsQ = useGetPlanDatasetsQuery(planId);
  const [savePrograma, saveState] = usePutProgramaMutation();

  // Local editable copy of the ordered rows; the server dataset seeds it and a
  // re-fetch (new plan / discard) reseeds it. Row order is the only state that
  // matters — seq + accumulated time are always re-derived from it.
  const [rows, setRows] = useState<ProgramaStep[]>([]);
  const [dirty, setDirty] = useState(false);

  // Coerce the server body to an array at the ingestion boundary: a real backend
  // can return a present-but-non-array `programa` (`{}`, `null`, an error
  // envelope) that a truthy `if (serverRows)` would admit — the next derive
  // `.map`/`.reduce` would then white-screen the app (the Oxbo-Z prod crash
  // class). withDerived also orders by wire `seq` so the seeded copy is in
  // execution order regardless of JSON order.
  const serverRows = datasetsQ.data?.programa;
  useEffect(() => {
    setRows(withDerived(serverRows));
    setDirty(false);
  }, [serverRows]);

  const derived = useMemo(() => withDerived(rows), [rows]);
  const totals = useMemo(() => programTotals(rows), [rows]);
  const points = useMemo(() => curvePoints(rows), [rows]);

  const plan = planQ.data;
  const enr = enrichEventCode(plan?.event_code);
  const opVar = enr.letter ? EVENT_LETTER_OP_VAR[enr.letter] : "--op-unconv";

  function commit(next: ProgramaStep[]) {
    setRows(next);
    setDirty(true);
  }

  async function save() {
    // Persist the re-sequenced, re-accumulated rows (server is authoritative on
    // seq + accumulated time, but we send our derivation so a headless save is
    // self-consistent).
    await savePrograma({ planId, rows: withDerived(rows) }).unwrap();
    setDirty(false);
  }

  const columns: GridColumn<DerivedStep>[] = [
    {
      key: "seq",
      label: t("program.col.seq"),
      width: 56,
      render: (r) => <span className="seqnum">{r.seq}</span>,
    },
    {
      key: "phase",
      label: t("program.col.phase"),
      render: (r, update) => (
        <input
          className="cell"
          value={r.phase}
          aria-label={t("program.col.phase")}
          placeholder={t("program.phasePlaceholder")}
          onChange={(e) => update({ phase: e.target.value })}
        />
      ),
    },
    {
      key: "code_description",
      label: t("program.col.description"),
      render: (r, update) => (
        <input
          className="cell"
          value={r.code_description ?? ""}
          aria-label={t("program.col.description")}
          placeholder={t("program.descPlaceholder")}
          onChange={(e) => update({ code_description: e.target.value || null })}
        />
      ),
    },
    numCol("depth_from", t("program.col.depthFrom")),
    numCol("depth_until", t("program.col.depthUntil")),
    numCol("time_hours", t("program.col.time")),
    numCol("dol", t("program.col.dol")),
    numCol("pct_completion", t("program.col.pct")),
    numCol("amount_usd", t("program.col.amount")),
    {
      key: "time_hours_accumulated",
      label: t("program.col.accTime"),
      numeric: true,
      render: (r) => (
        <span className="dim tab-nums">{fmtNum(r.time_hours_accumulated, locale)}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="screen-head">
        <h1>{t("program.title")}</h1>
        <span className="badge info">{t("program.badge")}</span>
        <div className="actions">
          <button type="button" className="btn primary" onClick={() => void save()} disabled={!dirty || saveState.isLoading}>
            <span className="ic ic-check" aria-hidden="true" />
            {saveState.isLoading ? t("program.saving") : t("program.save")}
          </button>
        </div>
      </div>

      {/* event / plan context */}
      {plan && (
        <div className="chip-ctx" style={{ marginBottom: 16, flexWrap: "wrap" }}>
          <span className="k">{t("program.ctx.well")}</span>
          <span className="mono">{plan.well_uwi}</span>
          <span className="dim">·</span>
          <span className="k">{t("program.ctx.event")}</span>
          <span className="letter-badge" style={{ background: `var(${opVar})` }}>
            <span className="sw" style={{ background: "var(--head)" }} />
            {enr.letter ?? "?"} · {plan.event_code}
          </span>
          {plan.afe_no && (
            <>
              <span className="dim">·</span>
              <span className="k">{t("program.ctx.afe")}</span>
              <span className="mono">{plan.afe_no}</span>
            </>
          )}
          <span className="verchip">v{plan.version}</span>
        </div>
      )}

      <div className="section-note">
        <span className="qm">i</span>
        <span>{t("program.note")}</span>
      </div>

      <QueryState
        isLoading={datasetsQ.isLoading}
        isError={datasetsQ.isError}
        onRetry={() => void datasetsQ.refetch()}
      >
        {/* KPI chips */}
        <div className="kpis">
          <Kpi v={fmtNum(totals.phases, locale)} l={t("program.kpi.phases")} />
          <Kpi v={fmtNum(totals.totalHours, locale)} u="h" l={t("program.kpi.totalTime")} />
          <Kpi v={fmtNum(totals.totalDol, locale, { maximumFractionDigits: 1 })} u="d" l={t("program.kpi.dol")} />
          <Kpi v={fmtNum(totals.finalDepth, locale)} u="m" l={t("program.kpi.finalDepth")} />
          <Kpi v={fmtMoney(totals.totalAmount, locale, { compact: true, currency: plan?.currency })} l={t("program.kpi.amount")} />
        </div>

        {/* Ordered-phases grid */}
        <section className="card" style={{ padding: 16, marginBottom: 20 }}>
          <div className="rowflex" style={{ marginBottom: 10 }}>
            <h2 style={{ margin: 0 }}>
              {t("program.grid.title")}{" "}
              <span className="dim" style={{ fontWeight: 400, fontSize: 13 }}>
                — {t("program.grid.hint")}
              </span>
            </h2>
          </div>

          <InlineRowGrid<DerivedStep>
            rows={derived}
            columns={columns}
            onChange={(next) => commit(next)}
            makeEmptyRow={(i) => ({ ...emptyStep(i), seq: i + 1, time_hours_accumulated: 0 })}
            addLabel={t("program.addPhase")}
            emptyLabel={t("program.empty")}
            footer={
              <div className="dt-total-note dim" style={{ fontSize: 11.5, margin: "8px 2px 0" }}>
                {t("program.total")}: {fmtNum(totals.totalHours, locale)} h ·{" "}
                {fmtNum(totals.totalDol, locale, { maximumFractionDigits: 1 })} d ·{" "}
                {fmtPct(100, locale)} · {fmtMoney(totals.totalAmount, locale, { currency: plan?.currency })}
              </div>
            }
          />
          <p className="dim" style={{ fontSize: 11.5, margin: "8px 2px 0" }}>
            {t("program.accHint")}
          </p>
        </section>

        {/* Plan curve — depth vs days */}
        <section className="card" style={{ padding: 16 }}>
          <div className="rowflex" style={{ marginBottom: 6 }}>
            <h2 style={{ margin: 0 }}>{t("program.curve.title")}</h2>
            <span className="dim" style={{ fontSize: 12.5 }}>
              {t("program.curve.sub")}
            </span>
          </div>

          <PlanCurveChart
            points={points}
            seriesVar={opVar}
            xLabel={t("program.curve.xLabel")}
            yLabel={t("program.curve.yLabel")}
            ariaLabel={t("program.curve.aria")}
          />
          <div className="legend">
            <span className="s" style={{ ["--series" as string]: `var(${opVar})` }}>
              {t("program.curve.legend")}
            </span>
          </div>
        </section>

        <p className="dim" style={{ fontSize: 12, marginTop: 16, lineHeight: 1.7 }}>
          {t("program.footnote")}
        </p>
      </QueryState>
    </div>
  );
}

/** A numeric editable-cell column bound to a ProgramaStep numeric field. */
function numCol(key: keyof ProgramaStep, label: string): GridColumn<DerivedStep> {
  return {
    key: key as string,
    label,
    numeric: true,
    render: (r, update) => (
      <input
        className="cell tab-nums"
        inputMode="decimal"
        style={{ textAlign: "right" }}
        value={r[key] == null ? "" : String(r[key])}
        aria-label={label}
        placeholder="0"
        onChange={(e) => {
          const raw = e.target.value.trim();
          const next = raw === "" ? null : Number(raw.replace(/,/g, ""));
          update({ [key]: raw === "" ? null : Number.isFinite(next) ? next : r[key] } as Partial<DerivedStep>);
        }}
      />
    ),
  };
}

function Kpi({ v, u, l }: { v: string; u?: string; l: string }) {
  return (
    <div className="kpi" data-own="p">
      <div className="v tab-nums">
        {v}
        {u && <span style={{ fontSize: 12, color: "var(--dim)" }}> {u}</span>}
      </div>
      <div className="l">{l}</div>
    </div>
  );
}
